import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion'
import type { Recipe } from '../../lib/supabase'
import {
  addMinute, clock, elapsedLabel, goTo, isUsed, lastUse, nextLabel, resetTimer, secondsLeft, tick, timerState, toggleTimer,
  toggleUsed, totalMinutes, type CookState,
} from '../../lib/cooking'
import { ingredientAmount } from '../../lib/recipe'
import { scaleFactorFor } from '../../lib/recipeMatch'
import { stepParts, stepUses } from '../../lib/stepUses'
import { safeHttpUrl } from '../../lib/url'
import { primeAlert, timerAlert } from '../../lib/timerAlert'
import { useWakeLock } from '../../hooks/useWakeLock'
import { useLayout } from '../../hooks/useLayout'
import { useOrientation } from '../../hooks/useOrientation'
import { CheckIcon, ChevronIcon, CloseIcon, ExternalIcon, PhoneTurnIcon, PlayIcon, TimerIcon } from '../icons'

export interface CookingModeProps {
  recipe: Recipe
  /** The servings to cook (the planned meal's, or the recipe page's): every amount is scaled to them. */
  servings: number
  /** Where this meal's cooking stands (a new session, or the one left open). */
  initial: CookState
  locale?: string
  /** Every change, so closing and opening again carries on (useCookingSessions). */
  onSession: (s: CookState) => void
  onClose: () => void
  onCooked: () => void
  /** Started from the plan, so the end offers "Mark as cooked". From a recipe page it is just Done. Default true. */
  planned?: boolean
}

const SWIPE_MIN = 60
const SWIPE_RATIO = 1.5
const SHAKE_MS = 600
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Cooking mode (feature spec 04): full screen and always dark, the screen kept
// on, made to be read from a step away with floury hands. Stages: "Before you
// start" (-1, an overview: every ingredient and its amount, nothing to tick),
// each step, "All steps done" (n). The step is large, the words that name an
// ingredient underlined, the next step a quiet line under it so you can read
// ahead without a tap.
//
// Every ingredient stays in view in its own column beside the step: this
// step's first, then still to come, then the used ones dimmed in place. Used
// is guessed — every step that names it is behind you (lib/cooking.ts isUsed)
// — and a tap on a row says otherwise, which sticks.
//
// A step with minutes has one big timer button. Timers run on wall-clock
// deadlines and keep counting on other steps, pinned in the top bar (a tap
// goes to their step); one ends with the lime card, a shake, the chime and a
// buzz (timerAlert, behind the Settings switch). Ticks once a second only
// while this is open. ← and → (Space for next) step with a keyboard.
//
// A phone cooks held sideways; held upright it asks to be turned — iOS lets a
// web app lock nothing, so that card is the lock; Android's installed app
// turns by itself (screen.orientation.lock). Desktop and iPad, whichever way up.
export function CookingMode(p: CookingModeProps) {
  useWakeLock()
  const desktop = useLayout() === 'desktop'
  const orientation = useOrientation()
  const upright = !desktop && orientation === 'portrait'
  const reduce = useReducedMotion()
  const [s, setS] = useState<CookState>(p.initial)
  const [now, setNow] = useState(() => Date.now())
  const [shaking, setShaking] = useState<number | null>(null)
  const latest = useRef(s)
  useEffect(() => { latest.current = s }, [s])
  const swipe = useRef<{ x: number; y: number } | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // What a screen reader hears: the stage it moved to, a timer that ended.
  const [announce, setAnnounce] = useState('')
  const shownStep = useRef(s.step)

  const steps = p.recipe.steps
  const n = steps.length
  const factor = scaleFactorFor(p.recipe, p.servings)
  const names = p.recipe.ingredients.map(g => g.name)
  // Each line's own amount, as on the recipe page — never merged by name.
  const qtyOf = (i: number) => ingredientAmount(p.recipe.ingredients[i], factor, p.locale)
  const last = useMemo(() => lastUse(steps, names), [steps, names])

  useEffect(() => { p.onSession(s) }, [s]) // eslint-disable-line react-hooks/exhaustive-deps
  // Where the browser can (Android's installed app), turn the screen for us.
  useEffect(() => {
    if (desktop) return
    const o = screen.orientation as (ScreenOrientation & { lock?: (to: string) => Promise<void> }) | undefined
    try { o?.lock?.('landscape')?.catch(() => {}) } catch { /* not allowed here: the card asks instead */ }
    return () => { try { o?.unlock?.() } catch { /* nothing locked */ } }
  }, [desktop])
  // Focus starts on ✕ and goes back to the Cook button on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => { if (opener && document.contains(opener)) opener.focus({ preventScroll: true }) }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      const t = Date.now()
      setNow(t)
      const r = tick(latest.current, t)
      if (r.state !== latest.current && Object.keys(r.state.timers).length) setS(r.state)
      if (r.finished.length) {
        timerAlert()
        setAnnounce(r.finished.map(k => `Step ${k + 1} timer done`).join('. '))
        setShaking(r.finished[0])
        setTimeout(() => setShaking(null), SHAKE_MS)
      }
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const go = (to: number) => setS(cur => goTo(cur, to, p.recipe))
  const onNext = () => (s.step === n ? p.onCooked() : go(s.step + 1))
  const stageLabel = (k: number) => (k === -1 ? 'Before you start' : k === n ? 'All steps done' : `Step ${k + 1}`)
  const stages = [-1, ...steps.map((_, i) => i), n]
  const others = Object.entries(s.timers).map(([k, t]) => ({ k: +k, t })).filter(({ k }) => k !== s.step)
  const href = safeHttpUrl(p.recipe.url)
  // The ingredients beside the step (the overview lists them itself).
  const panel = s.step >= 0 && s.step < n && p.recipe.ingredients.length > 0

  useEffect(() => {
    if (shownStep.current === s.step) return
    shownStep.current = s.step
    setAnnounce(s.step === -1 ? 'Before you start. Get everything out'
      : s.step === n ? 'All steps done. Ready to eat'
      : `Step ${s.step + 1} of ${n}: ${steps[s.step].text}`)
  }, [s.step, n, steps])

  // ← and → step; Space goes on, unless a button has focus (Space presses it).
  function stepKeys(e: React.KeyboardEvent): boolean {
    if (e.metaKey || e.ctrlKey || e.altKey) return false
    const onButton = (e.target as HTMLElement).closest?.('button, a, input')
    if (e.key === 'ArrowRight' || (e.key === ' ' && !onButton)) { e.preventDefault(); onNext(); return true }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(s.step - 1); return true }
    return false
  }

  // Tab and Shift+Tab wrap inside: the app behind is covered, not reachable.
  function trapTab(e: React.KeyboardEvent) {
    if (e.key !== 'Tab' || !rootRef.current) return
    const all = [...rootRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(el => !el.closest('[inert]'))
    if (!all.length) return
    const first = all[0], last = all[all.length - 1]
    const at = document.activeElement
    if (e.shiftKey ? at === first || !rootRef.current.contains(at) : at === last) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    }
  }

  function body() {
    if (s.step === -1) {
      const minutes = totalMinutes(p.recipe)
      const facts = [
        `${n} ${n === 1 ? 'step' : 'steps'}`,
        minutes > 0 && `about ${minutes} min`,
        `${p.recipe.ingredients.length} ${p.recipe.ingredients.length === 1 ? 'ingredient' : 'ingredients'}`,
      ].filter(Boolean).join(', ')
      return (
        <>
          <p className="ml-label ml-cook__kicker">Before you start</p>
          <h2 className="ml-display ml-cook__headline">Get everything out</h2>
          <p className="ml-cook__sub">{facts}</p>
          <ul className="ml-cook__overview" aria-label="Ingredients">
            {p.recipe.ingredients.map((g, i) => (
              <li key={i}>
                <span className="ml-cook__name">{g.name}</span>
                <span className="ml-cook__qty ml-num">{qtyOf(i)}</span>
              </li>
            ))}
          </ul>
        </>
      )
    }
    if (s.step === n) {
      const minutes = Math.max(1, Math.floor((now - s.startedAt) / 60_000))
      return (
        <>
          <p className="ml-label ml-cook__kicker">All steps done</p>
          <h2 className="ml-display ml-cook__headline ml-cook__headline--big">Ready to eat</h2>
          <ul className="ml-cook__stats" aria-label="Summary">
            <li><span className="ml-cook__stat ml-num is-accent">{minutes}</span><span className="ml-label">Minutes</span></li>
            <li><span className="ml-cook__stat ml-num">{n}</span><span className="ml-label">Steps</span></li>
            <li><span className="ml-cook__stat ml-num">{p.servings}</span><span className="ml-label">Servings</span></li>
          </ul>
          <p className="ml-cook__sub">{p.planned === false ? 'Enjoy.' : "Enjoy. Mark it as cooked to keep track of what you've made this week."}</p>
          {n === 0 && href && (
            <a className="ml-cook__link" href={href} target="_blank" rel="noopener noreferrer">Open recipe page <ExternalIcon size={16} /></a>
          )}
        </>
      )
    }
    const step = steps[s.step]
    const timer = s.timers[s.step]
    const total = timer?.total ?? (step.minutes ?? 0) * 60
    const left = timer ? secondsLeft(timer, now) : total
    const state = timerState(timer)
    const verb = !timer ? 'Start' : timer.done ? 'Restart' : state === 'Running' ? 'Pause' : 'Resume'
    const next = steps[s.step + 1]
    return (
      <>
        <p className="ml-label ml-cook__kicker">Step {s.step + 1} of {n}</p>
        <p className="ml-cook__step">
          {stepParts(step.text, names).map((part, k) => (part.mark ? <mark key={k} className="ml-cook__mark">{part.text}</mark> : part.text))}
        </p>
        {step.minutes != null && step.minutes > 0 && (
          <div role="group" aria-label="Timer"
            className={['ml-timer', timer && 'is-on', timer?.done && 'is-done', shaking === s.step && !reduce && 'is-shaking'].filter(Boolean).join(' ')}>
            <button type="button" className="ml-timer__main" aria-label={`${verb} timer`}
              onClick={() => { primeAlert(); setS(cur => toggleTimer(cur, cur.step, p.recipe, Date.now())) }}>
              {!timer && <PlayIcon size={22} />}
              <span className="ml-timer__clock ml-num">{timer?.done ? 'Done' : clock(left)}</span>
              <span className="ml-timer__verb">{verb}</span>
            </button>
            {timer && (
              <div className="ml-timer__aux-row">
                <button type="button" className="ml-timer__aux" onClick={() => setS(cur => addMinute(cur, cur.step, p.recipe, Date.now()))}>+1 min</button>
                <button type="button" className="ml-timer__aux" onClick={() => setS(cur => resetTimer(cur, cur.step))}>Reset</button>
              </div>
            )}
            {timer && !timer.done && (
              <div className="ml-timer__bar" aria-hidden="true">
                <span style={{ width: `${total ? ((total - left) / total) * 100 : 0}%` }} />
              </div>
            )}
          </div>
        )}
        {next && (
          <p className="ml-cook__next-up"><span className="ml-label">Next</span> <span className="ml-cook__next-text">{next.text}</span></p>
        )}
      </>
    )
  }

  return (
    <div ref={rootRef} className="ml-cook ml-dark-scope" role="dialog" aria-modal="true" aria-label={`Cooking ${p.recipe.title}`}
      onKeyDown={e => { if (e.key === 'Escape') p.onClose(); else if (!upright && !stepKeys(e)) trapTab(e) }}>
      <p className="ml-visually-hidden" role="status">{announce}</p>
      <header className="ml-cook__top">
        <button ref={closeRef} type="button" className="ml-cook__close" aria-label="Close cooking mode" onClick={p.onClose}>
          <CloseIcon size={22} />
        </button>
        <div className="ml-cook__title">
          <span className="ml-cook__recipe">{p.recipe.title}</span>
          <span className="ml-cook__servings">{p.servings} servings</span>
        </div>
        {!upright && others.length > 0 && (
          <div className="ml-cook__pills">
            {others.map(({ k, t }) => (
              <button key={k} type="button" className={`ml-cook__pill${t.done ? ' is-done' : ''}`}
                aria-label={`Step ${k + 1} ${t.done ? 'done' : clock(secondsLeft(t, now))}`} onClick={() => go(k)}>
                <TimerIcon size={16} /><span>Step {k + 1}</span><span className="ml-num">{t.done ? 'Done' : clock(secondsLeft(t, now))}</span>
              </button>
            ))}
          </div>
        )}
        <span className="ml-cook__elapsed ml-num" aria-label="Cooking for">{elapsedLabel(s.startedAt, now)}</span>
      </header>

      {upright ? (
        <div className="ml-cook__rotate" role="note">
          <PhoneTurnIcon size={56} className="ml-cook__rotate-icon" />
          <p className="ml-display ml-cook__rotate-title">Turn your phone sideways</p>
          <p className="ml-cook__sub">The ingredients stay beside each step, so you can tick them off as they go in.</p>
        </div>
      ) : (
        <>
          <nav className="ml-cook__progress" aria-label="Stages">
            {stages.map(k => (
              <button key={k} type="button" aria-label={stageLabel(k)} aria-current={k === s.step ? 'step' : undefined}
                className={`ml-cook__seg${k < s.step ? ' is-past' : k === s.step ? ' is-current' : ''}`} onClick={() => go(k)} />
            ))}
          </nav>

          <div className="ml-cook__main">
            {panel && (
              <div className="ml-cook__side">
                <IngredientPanel names={names} qtyOf={qtyOf} isUsed={i => isUsed(s, i, last)}
                  now={stepUses(steps[s.step].text, names)}
                  onToggle={i => setS(cur => toggleUsed(cur, i, last))} />
              </div>
            )}

            <div className="ml-cook__stage" data-testid="cook-stage"
              onPointerDown={e => { swipe.current = { x: e.clientX, y: e.clientY } }}
              onPointerUp={e => {
                const from = swipe.current
                swipe.current = null
                if (!from) return
                const dx = e.clientX - from.x
                const dy = e.clientY - from.y
                if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > SWIPE_RATIO * Math.abs(dy)) go(s.step + (dx < 0 ? 1 : -1))
              }}>
              <AnimatePresence mode="popLayout" initial={false} custom={s.direction}>
                <Stage key={s.step} direction={s.direction} reduce={!!reduce}>{body()}</Stage>
              </AnimatePresence>
            </div>
          </div>

          <footer className="ml-cook__foot">
            <button type="button" className="ml-cook__back" disabled={s.step === -1} onClick={() => go(s.step - 1)}>Back</button>
            <button type="button" className="ml-cook__next" onClick={onNext}>
              {n === 0 && s.step === -1 ? 'Start cooking' : nextLabel(s.step, n, p.planned !== false)} <ChevronIcon size={20} />
            </button>
          </footer>
        </>
      )}
    </div>
  )
}

/** One stage, sliding in from the direction of travel (none under reduced
 *  motion). While it slides out it is hidden from assistive tech and inert, so
 *  only the stage being shown is read and reachable. */
function Stage({ direction, reduce, children }: { direction: 'forward' | 'back'; reduce: boolean; children: React.ReactNode }) {
  const present = useIsPresent()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { if (ref.current) ref.current.inert = !present }, [present])
  return (
    <motion.div ref={ref} className="ml-cook__body" aria-hidden={present ? undefined : true}
      initial={reduce ? false : { x: direction === 'forward' ? 60 : -60, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={reduce ? { opacity: 0 } : { x: direction === 'forward' ? -60 : 60, opacity: 0 }}
      transition={{ duration: reduce ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] }}>
      {children}
    </motion.div>
  )
}

/** Every ingredient beside the step, in view the whole time: this step's
 *  first (lime), then still to come, then the used ones dimmed in place. A tap
 *  on a row says used or not, whatever the guess. */
function IngredientPanel({ names, qtyOf, now, isUsed, onToggle }: {
  names: string[]
  qtyOf: (i: number) => string
  /** The current step's ingredients (lib/stepUses.ts). */
  now: number[]
  isUsed: (i: number) => boolean
  onToggle: (i: number) => void
}) {
  const all = names.map((_, i) => i)
  const groups = [
    { label: 'This step', rows: all.filter(i => now.includes(i) && !isUsed(i)) },
    { label: 'Still to come', rows: all.filter(i => !now.includes(i) && !isUsed(i)) },
    { label: 'Used', rows: all.filter(i => isUsed(i)) },
  ].filter(g => g.rows.length > 0)
  return (
    <section className="ml-cook__side-ings" aria-label="Ingredients">
      {groups.map(g => (
        <div key={g.label} className="ml-cook__group">
          <h2 className="ml-label ml-cook__group-label">{g.label}</h2>
          <ul className="ml-cook__ings" aria-label={g.label}>
            {g.rows.map(i => {
              const used = isUsed(i)
              return (
                <li key={i}>
                  <button type="button" role="checkbox" aria-checked={used} onClick={() => onToggle(i)}
                    className={`ml-cook__ing${now.includes(i) && !used ? ' is-now' : ''}${used ? ' is-used' : ''}`}>
                    <span className="ml-cook__name">{names[i]}</span>
                    <span className="ml-cook__qty ml-num">{qtyOf(i)}</span>
                    {used && <CheckIcon size={16} />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </section>
  )
}
